import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { ChromePicker } from 'react-color';
import {
  DragSource,
  DropTarget,
} from 'react-dnd';
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  Input,
} from 'reactstrap';
import { useIntl } from 'react-intl';

const ColorEditorItem = ({
  color,
  canReorder,
  onChange,
  onDelete,
  connectDragSource,
  connectDropTarget,
}) => {
  const intl = useIntl();
  const [pickerOpen, setPickerOpen] = useState(false);
  const itemRef = useRef(null);

  const handleSwatchClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setPickerOpen(true);
  };

  const handleSwatchPointerDown = (event) => {
    // Field wrappers can behave like labels and redirect focus to the first input.
    // Prevent default pointer handling so only the swatch interaction controls the picker.
    event.preventDefault();
    event.stopPropagation();
  };

  const handleSwatchKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setPickerOpen(true);
    }
  };

  useEffect(() => {
    if (!pickerOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (itemRef.current && !itemRef.current.contains(event.target)) {
        setPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [pickerOpen]);

  const dragHandle = canReorder ? connectDragSource(
    <span className="puck-colors-drag-handle">
      <i className="fas fa-grip-vertical" />
    </span>,
  ) : null;

  return connectDropTarget((
    <div
      className="puck-colors-editor-item"
      ref={itemRef}
    >
      <div className="puck-colors-editor-row">
        {dragHandle}
        <div
          role="button"
          tabIndex={0}
          aria-label={intl.formatMessage({ defaultMessage: 'Pick color' })}
          className="puck-colors-swatch-btn"
          style={{ background: color.hex }}
          onMouseDown={handleSwatchPointerDown}
          onTouchStart={handleSwatchPointerDown}
          onClick={handleSwatchClick}
          onKeyDown={handleSwatchKeyDown}
        />
        <Input
          type="text"
          bsSize="sm"
          value={color.hex}
          onChange={(e) => onChange({ ...color, hex: e.target.value })}
          onFocus={() => setPickerOpen(false)}
          className="puck-colors-hex-input"
          placeholder="#000000"
        />
        <Button
          type="button"
          color="link"
          size="sm"
          className="text-danger p-0 px-1"
          onClick={onDelete}
          title={intl.formatMessage({ defaultMessage: 'Remove color' })}
        >
          <i className="fas fa-trash mr-0" />
        </Button>
      </div>

      {pickerOpen && (
        <div className="puck-colors-picker">
          <ChromePicker
            color={color.hex}
            onChange={(c) => onChange({ ...color, hex: c.hex })}
            disableAlpha
          />
        </div>
      )}

      <Input
        type="text"
        bsSize="sm"
        value={color.title || ''}
        onChange={(e) => onChange({ ...color, title: e.target.value })}
        onFocus={() => setPickerOpen(false)}
        placeholder={intl.formatMessage({ defaultMessage: 'Color name (e.g. Primary Blue)' })}
        className="mb-1"
      />
      <Input
        type="text"
        bsSize="sm"
        value={color.subtitle || ''}
        onChange={(e) => onChange({ ...color, subtitle: e.target.value })}
        onFocus={() => setPickerOpen(false)}
        placeholder={intl.formatMessage({ defaultMessage: 'Subtitle (e.g. Used for CTAs)' })}
      />
    </div>
  ));
};

ColorEditorItem.propTypes = {
  color: PropTypes.shape({
    hex: PropTypes.string.isRequired,
    title: PropTypes.string,
    subtitle: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  canReorder: PropTypes.bool.isRequired,
  moveColor: PropTypes.func.isRequired,
  getIndexByKey: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
};

const puckColorSource = {
  beginDrag(props) {
    return {
      id: props.color.colorKey,
      index: props.index,
    };
  },

  canDrag(props) {
    return props.canReorder;
  },
};

const puckColorTarget = {
  hover(props, monitor) {
    const dragItem = monitor.getItem();
    const dragIndex = props.getIndexByKey(dragItem.id);
    const hoverIndex = props.index;

    if (dragIndex < 0) {
      return;
    }

    if (dragItem.id === props.color.colorKey || dragIndex === hoverIndex) {
      return;
    }

    props.moveColor(dragIndex, hoverIndex);
  },
};

const DraggableColorEditorItem =
DragSource('puckColorItem', puckColorSource, (DNDconnect) => ({
  connectDragSource: DNDconnect.dragSource(),
}))(DropTarget('puckColorItem', puckColorTarget, DNDconnect => ({
  connectDropTarget: DNDconnect.dropTarget(),
}))(ColorEditorItem));

const BrandColorsField = ({ value, onChange, field }) => {
  const intl = useIntl();
  const colors = Array.isArray(value) ? value : [];

  const handleAdd = () => {
    onChange([...colors, {
      colorKey: uuidv4(), hex: '#000000', title: '', subtitle: '',
    }]);
  };

  const handleChange = (key, updated) => {
    onChange(colors.map((c) => (c.colorKey === key ? updated : c)));
  };

  const handleDelete = (key) => {
    onChange(colors.filter((c) => c.colorKey !== key));
  };

  const moveColor = (from, to) => {
    if (from === to) return;
    const reordered = [...colors];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    onChange(reordered);
  };

  const canReorder = colors.length > 1;
  const getIndexByKey = key => colors.findIndex(c => c.colorKey === key);

  return (
    <div className="puck-colors-field-wrap">
      <div className="puck-colors-field-label">
        {field.label || intl.formatMessage({ defaultMessage: 'Brand Colors' })}
      </div>
      <div className="puck-colors-field">
        {colors.map((color, index) => (
          <DraggableColorEditorItem
            key={color.colorKey || `color-${index}`}
            color={color}
            index={index}
            canReorder={canReorder}
            moveColor={moveColor}
            getIndexByKey={getIndexByKey}
            onChange={(updated) => handleChange(color.colorKey, updated)}
            onDelete={() => handleDelete(color.colorKey)}
          />
        ))}
        <Button
          type="button"
          color="primary"
          size="sm"
          block
          onClick={handleAdd}
          className="mt-1"
        >
          {intl.formatMessage({ defaultMessage: '+ Add Color' })}
        </Button>
      </div>
    </div>
  );
};

BrandColorsField.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({ label: PropTypes.string }).isRequired,
};

BrandColorsField.defaultProps = {
  value: [],
};

export default BrandColorsField;
